import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, Plus } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useAuth, useInstitutionListStore } from "@/store";
import { UserRole } from "@/services/types/common.types";
import { SearchableSelect } from "@/shared/components/common/SearchableSelect";
import { useTenantOptions } from "../hooks/useTenantOptions";
import { INSTITUTION_STATUS_FILTER_OPTIONS } from "../constants/institutionStatus";
import { INSTITUTION_LISTING_CONFIG } from "../constants/listingConfig";

const ALL_VALUE = "__all__";
const ALL_TENANTS_VALUE = "__all_tenants__";

export function InstitutionHeader() {
  const { t } = useTranslation("institutions");
  const navigate = useNavigate();
  const searchQuery = useInstitutionListStore((s) => s.searchQuery);
  const setSearchQuery = useInstitutionListStore((s) => s.setSearchQuery);
  const status = useInstitutionListStore((s) => s.status);
  const setStatus = useInstitutionListStore((s) => s.setStatus);
  const tenantId = useInstitutionListStore((s) => s.tenantId);
  const setTenantId = useInstitutionListStore((s) => s.setTenantId);

  // Tenant admins are already limited to their own tenant, so only others get the filter.
  const { hasRole } = useAuth();
  const showTenantFilter =
    !hasRole(UserRole.ADMIN) || hasRole(UserRole.PLATFORM_ADMIN);
  const { data: tenantOptions } = useTenantOptions({ enabled: showTenantFilter });
  const tenantFilterOptions = useMemo(() => {
    const options = [
      { value: ALL_TENANTS_VALUE, label: t("list.filter.tenantAll") },
      ...(tenantOptions ?? []),
    ];
    // A selected tenant that is not in the loaded list stays visible.
    if (tenantId && !options.some((o) => o.value === tenantId)) {
      options.push({ value: tenantId, label: `#${tenantId}` });
    }
    return options;
  }, [tenantOptions, tenantId, t]);

  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="relative w-[320px]">
          <Input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label={t("list.searchLabel")}
            placeholder={t("list.searchPlaceholder")}
            className="w-full pr-10 pl-3 text-sm"
          />
          <Search
            className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
        </div>
        {showTenantFilter ? (
          <div className="w-[240px]">
            <SearchableSelect
              value={tenantId || ALL_TENANTS_VALUE}
              onValueChange={(next) =>
                setTenantId(next === ALL_TENANTS_VALUE ? "" : next)
              }
              options={tenantFilterOptions}
              placeholder={t("list.filter.tenant")}
              searchPlaceholder={t("list.filter.tenantSearch")}
              viewAllLabel={t("list.filter.viewAll")}
              aria-label={t("list.filter.tenant")}
            />
          </div>
        ) : null}
        <Select
          value={status || ALL_VALUE}
          onValueChange={(next) => setStatus(next === ALL_VALUE ? "" : next)}
        >
          <SelectTrigger
            className="h-9 w-[180px]"
            aria-label={t("list.filter.status")}
          >
            <SelectValue placeholder={t("list.filter.status")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>
              {t("list.filter.allStatuses")}
            </SelectItem>
            {INSTITUTION_STATUS_FILTER_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {t(`status.${opt.value}`, { defaultValue: opt.label })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button
        type="button"
        variant="outline"
        onClick={() => navigate(`${INSTITUTION_LISTING_CONFIG.basePath}/add`)}
        className="h-9 border-primary bg-background px-4 text-primary hover:bg-accent hover:text-accent-foreground"
      >
        <Plus className="h-4 w-4" aria-hidden />
        <span>{t("list.add")}</span>
      </Button>
    </div>
  );
}
